import {
  Avatar,
  Box,
  Button,
  Menu as MuiMenu,
  MenuItem,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { onlyLoggedOutRoutes, protectedRoutes, publicRoutes } from "./routes";
import { PUBLIC_POLICY_METADATA } from "Pages/Legal/policyMetadata";
import BackToTop from "Components/BackToTop/BackToTop";
import ScrollToTop from "Components/ScrollToTop/ScrollToTop";
import CookieBanner from "Components/CookieBanner";
import { ErrorBoundary } from "react-error-boundary";
import Footer from "./Components/Footer/Footer";
import FeedbackButton from "./Components/FeedbackButton/FeedbackButton";
import Header from "./Components/Header/Header";
import LoadingFallback from "./Components/LoadingFallback/LoadingFallback";
import NoPage from "./Components/NoPage/NoPage";
import HomeSkeleton from "Pages/Home/HomeSkeleton";
import ProfileCompletionReminder from "Components/ProfileCompletionReminder/ProfileCompletionReminder";
import AnnouncementSlot from "Components/AnnouncementBanner/AnnouncementSlot";
import OnlyForLoggedOut from "./Components/RoutesWrapper/OnlyForLoggedOut";
import ProtectedRoute from "./Components/RoutesWrapper/ProtectedRoute";
import axios from "axios";
import { baseURL } from "./Shared/baseURL";
import {
  attachApiInterceptors,
  PENDING_TOAST_KEY,
} from "./Shared/apiInterceptors";
import { projectName } from "./theme/themeConfig";
import { useNotify, useReduxUser, useReduxUserProfile } from "./Utils/hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  getProfile,
  LOGOUT_IN_PROGRESS_KEY,
  LOGOUT_IN_PROGRESS_TTL_MS,
  userLogout,
} from "Redux/Actions";
import { SET_ENTITLEMENTS } from "./Redux/ActionTypes";
import {
  RegistrationProvider,
  useRegistration,
} from "./contexts/RegistrationContext";
import RegistrationCompletionModal from "./Components/RegistrationCompletionModal";
import keycloak from "./keycloak";
import { useUserEntitlements } from "./Utils/queries";
import { useFirebaseNotifications } from "./hooks/useFirebaseNotifications";
import {
  clearGuestProgress,
  readGuestProgress,
} from "Utils/guestProgressStorage";
import { useGTMTracking } from "hooks/useGTMTracking";

// default baseURL
axios.defaults.baseURL = baseURL;

const normalizePathname = (pathname = "") => {
  const normalized = String(pathname || "").replace(/\/+$/, "");
  return normalized || "/";
};

const isSharedResultPath = (pathname = "") =>
  /^\/(?:games\/shared-results|results\/shared)\//.test(
    normalizePathname(pathname),
  );

const sharedGuestGamePaths = new Set([
  "/games/results",
  "/games/match-game",
  "/games/multiple-choice",
  "/games/scrambled-keywords",
]);
const gamePlayPaths = new Set([
  "/games/match-game",
  "/games/multiple-choice",
  "/games/scrambled-keywords",
]);
const gamePlayPathPrefixes = ["/challenge-hub/challenge-details/"];
const sharedGuestQueryKeys = [
  "sharedResultToken",
  "sharedResulttoken",
  "shared_result_token",
  "shared-result-token",
];

const isSharedGuestGamePath = (pathname = "", search = "") => {
  if (!sharedGuestGamePaths.has(normalizePathname(pathname))) return false;
  const params = new URLSearchParams(search || "");
  if (params.get("guest") === "1") return true;
  return sharedGuestQueryKeys.some((key) =>
    String(params.get(key) || "").trim(),
  );
};

const isGamePlayPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);
  return (
    gamePlayPaths.has(normalized) ||
    gamePlayPathPrefixes.some((prefix) => normalized.startsWith(prefix))
  );
};

const isChallengeHubPlayPath = (pathname = "", search = "") => {
  if (normalizePathname(pathname) !== "/challenge-hub") return false;
  const params = new URLSearchParams(search || "");
  return params.get("start") === "1" && Boolean(params.get("challengeId"));
};

const publicSupportPaths = [
  "/support/kail",
  "/policies/ai-usage",
  "/policies/privacy",
  "/policies/safeguarding",
  "/contact",
];

const registrationModalExemptPaths = new Set([
  "/policies",
  "/disclaimer",
  ...publicSupportPaths,
  ...PUBLIC_POLICY_METADATA.map((policy) => policy.path),
]);

const isRegistrationModalExemptPath = (pathname) =>
  registrationModalExemptPaths.has(normalizePathname(pathname));

const getRegistrationCompletionIdentities = (user, keycloakSubject) =>
  [user?.id, user?.uuid, user?.userId, keycloakSubject, user?.email].filter(
    (value) => value !== null && value !== undefined && value !== "",
  );

const getRegistrationRoleKey = (user) => {
  const raw = String(
    user?.role ||
      user?.accountType ||
      user?.roleDetails?.role ||
      user?.profile?.role ||
      "",
  ).toLowerCase();
  if (raw.includes("coordinator")) return "coordinator";
  if (raw.includes("mentor")) return "mentor";
  if (raw.includes("lecturer")) return "lecturer";
  if (raw.includes("teacher")) return "teacher";
  if (raw.includes("learner")) return "learner";
  return "";
};

const hasRegistrationValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const getRegistrationInstitution = (user) =>
  user?.roleDetails?.institution &&
  typeof user.roleDetails.institution === "object"
    ? user.roleDetails.institution
    : {
        institutionType: user?.institutionType,
        name: user?.institutionName || user?.studiesAt,
      };

const getRegistrationOrganization = (user) =>
  user?.roleDetails?.organization &&
  typeof user.roleDetails.organization === "object"
    ? user.roleDetails.organization
    : user?.organization && typeof user.organization === "object"
      ? user.organization
      : { name: user?.organisationName || user?.organizationName };

const getRegistrationSubjectIds = (user) => {
  const roleDetails = user?.roleDetails || {};
  if (Array.isArray(roleDetails.selectedSubjectIds)) {
    return roleDetails.selectedSubjectIds;
  }
  if (Array.isArray(roleDetails.expertise)) {
    return roleDetails.expertise
      .map((entry) => entry?.subjectId)
      .filter((subjectId) => subjectId !== null && subjectId !== undefined);
  }
  if (Array.isArray(user?.selectedSubjects)) return user.selectedSubjects;
  if (Array.isArray(user?.subjects)) return user.subjects;
  return [];
};

const getRegistrationJobTitle = (user) => {
  const expertise = Array.isArray(user?.roleDetails?.expertise)
    ? user.roleDetails.expertise
    : [];
  return (
    user?.jobTitle ||
    user?.professionalExpertise ||
    expertise.map((entry) => entry?.jobTitle).find(hasRegistrationValue)
  );
};

const getRequiredRegistrationMissingLabels = (user) => {
  if (!user) return ["Registration details"];
  const roleKey = getRegistrationRoleKey(user);
  const missing = [];
  const requireField = (label, value) => {
    if (!hasRegistrationValue(value)) missing.push(label);
  };

  requireField("Country of birth", user?.countryOfBirth);
  requireField("Subjects", getRegistrationSubjectIds(user));

  if (["learner", "teacher", "lecturer"].includes(roleKey)) {
    const institution = getRegistrationInstitution(user);
    const institutionType = institution?.institutionType;
    requireField("Institution type", institutionType);
    if (
      hasRegistrationValue(institutionType) &&
      !["INDEPENDENT_LEARNER", "INDEPENDENT_EDUCATOR"].includes(
        String(institutionType).toUpperCase(),
      )
    ) {
      requireField("Institution name", institution?.name);
    }
  }

  if (["teacher", "lecturer"].includes(roleKey)) {
    requireField("Job title", getRegistrationJobTitle(user));
  }

  if (roleKey === "mentor") {
    requireField("Role", user?.roleDetails?.mentorRole || user?.mentorRole);
    requireField("Organisation name", getRegistrationOrganization(user)?.name);
    requireField("Job title", getRegistrationJobTitle(user));
  }

  if (roleKey === "coordinator") {
    requireField(
      "Role",
      user?.roleDetails?.coordinatorRole ||
        user?.coordinatorRole ||
        user?.detailedRole,
    );
    requireField("Organisation name", getRegistrationOrganization(user)?.name);
    requireField("Job title", getRegistrationJobTitle(user));
  }

  if (!roleKey) {
    requireField("Role", roleKey);
  }

  return Array.from(new Set(missing));
};

const getIsLogoutInProgress = () => {
  try {
    const raw = sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY);
    if (!raw) return false;

    const startedAt = Number(raw);
    return (
      Number.isFinite(startedAt) &&
      Date.now() - startedAt < LOGOUT_IN_PROGRESS_TTL_MS
    );
  } catch {
    return false;
  }
};

const App = () => {
  useGTMTracking();
  // state
  const [renderHeaderFooter, setRenderHeaderFooter] = useState(true);
  const [isLogoutInProgress, setIsLogoutInProgress] = useState(
    getIsLogoutInProgress,
  );
  const guestClaimAttemptedRef = useRef(false);

  // router
  const location = useLocation();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === "/";
  const isRegistrationPage = location.pathname === "/register";
  const isSharedResultPage = isSharedResultPath(location.pathname);
  const isSharedGuestGamePage = isSharedGuestGamePath(
    location.pathname,
    location.search,
  );
  const isGamePlayPage =
    isGamePlayPath(location.pathname) ||
    isChallengeHubPlayPath(location.pathname, location.search);
  const isStandalonePublicPage =
    normalizePathname(location.pathname) === "/disclaimer";
  const isRegistrationExemptPage =
    isRegistrationPage ||
    isSharedResultPage ||
    isSharedGuestGamePage ||
    isRegistrationModalExemptPath(location.pathname);
  const showSuccessModal = location.state?.registrationSuccess === true;
  const hideShellForLogout = isLogoutInProgress && !isLandingPage;
  const hideAppShell =
    hideShellForLogout ||
    isGamePlayPage ||
    isSharedResultPage ||
    isSharedGuestGamePage;
  const routeLoadingFallback = isLandingPage ? (
    <HomeSkeleton />
  ) : (
    <LoadingFallback />
  );

  // redux
  const userRedux = useReduxUser();
  const dispatch = useDispatch();
  const notify = useNotify();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const isAuthLoading = useSelector((state) => state.auth?.isLoading);
  const keycloakSubject =
    keycloak?.authenticated && keycloak?.tokenParsed?.sub
      ? keycloak.tokenParsed.sub
      : null;
  const registrationCompletionIdentities = useMemo(
    () => getRegistrationCompletionIdentities(userRedux, keycloakSubject),
    [keycloakSubject, userRedux],
  );
  const requiredRegistrationMissingFields = useMemo(
    () => getRequiredRegistrationMissingLabels(userRedux),
    [userRedux],
  );
  const registrationCompletedBackendWithRequiredFields = Boolean(
    userRedux?.registrationCompleted === true &&
    requiredRegistrationMissingFields.length === 0,
  );
  const registrationMinimumCompleted = Boolean(
    requiredRegistrationMissingFields.length === 0,
  );
  const registrationStorageKey = isAuthenticated
    ? `user-${keycloakSubject || userRedux?.id || "auth"}`
    : "guest";
  const shouldResetRegistration = Boolean(
    isAuthenticated &&
    userRedux?.id &&
    requiredRegistrationMissingFields.length > 0 &&
    !isRegistrationExemptPage,
  );
  const registrationGateActive = Boolean(
    isAuthenticated && userRedux && !registrationMinimumCompleted,
  );

  const { data: entitlementsData } = useUserEntitlements(userRedux?.id, {
    enabled: Boolean(isAuthenticated && userRedux?.id),
  });

  useFirebaseNotifications({
    enabled: Boolean(isAuthenticated),
    userId: userRedux?.id,
  });

  useEffect(() => {
    if (entitlementsData) {
      dispatch({
        type: SET_ENTITLEMENTS,
        payload: entitlementsData,
      });
    }
  }, [dispatch, entitlementsData]);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || guestClaimAttemptedRef.current) {
      return;
    }

    const pendingGuestProgress = readGuestProgress();
    if (!pendingGuestProgress?.claimToken) return;

    let cancelled = false;
    guestClaimAttemptedRef.current = true;

    const claim = async () => {
      try {
        const { claimGuestProgress } = await import("./api/gameApi");
        await claimGuestProgress(pendingGuestProgress.claimToken);
        if (cancelled) return;

        const params = new URLSearchParams(location.search || "");
        [
          "guest",
          "sharedResultToken",
          "sharedResulttoken",
          "shared_result_token",
          "shared-result-token",
          "challengeScore",
          "guestScore",
          "guestCorrect",
          "guestTotal",
          "guestPoints",
          "guestTimeSec",
          "guestSessionId",
          "guestAuthRequired",
          "guestResultLocked",
          "guestAlreadyPlayed",
          "guestMessage",
          "locked",
        ].forEach((key) => params.delete(key));

        if (location.pathname === "/games/results") {
          const search = params.toString();
          navigate(`${location.pathname}${search ? `?${search}` : ""}`, {
            replace: true,
          });
        } else if (pendingGuestProgress.gameId != null) {
          const resultParams = new URLSearchParams({
            gameId: String(pendingGuestProgress.gameId),
          });
          if (pendingGuestProgress.gameType) {
            resultParams.set("type", pendingGuestProgress.gameType);
          }
          if (pendingGuestProgress.listId != null) {
            resultParams.set("listId", String(pendingGuestProgress.listId));
          }
          if (pendingGuestProgress.subjectId != null) {
            resultParams.set(
              "subjectId",
              String(pendingGuestProgress.subjectId),
            );
          }
          navigate(`/games/results?${resultParams.toString()}`, {
            replace: true,
          });
        }
      } catch (error) {
        if (Number(error?.response?.status) === 409) {
          clearGuestProgress();
          return;
        }
        guestClaimAttemptedRef.current = false;
      }
    };

    void claim();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isAuthLoading,
    location.pathname,
    location.search,
    navigate,
    userRedux?.id,
    userRedux?.registrationCompleted,
  ]);

  useEffect(() => {
    setRenderHeaderFooter(true);
    sessionStorage.setItem("tutorialDialog", JSON.stringify([]));
    // eslint-disFle-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated || location.pathname !== "/") {
      return;
    }

    try {
      sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    } catch {}
    setIsLogoutInProgress(false);
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!registrationGateActive) {
      return;
    }

    if (isRegistrationPage) {
      return;
    }

    if (
      isSharedResultPage ||
      isSharedGuestGamePage ||
      isRegistrationModalExemptPath(location.pathname)
    ) {
      return;
    }

    navigate("/register", {
      replace: true,
      state: { registrationGateRedirect: true },
    });
  }, [
    isRegistrationPage,
    isSharedGuestGamePage,
    isSharedResultPage,
    location.pathname,
    navigate,
    registrationGateActive,
  ]);

  // Safety net: after auth callback, /me can lag briefly. Re-try hydration so
  // registration modal logic has a backend user id without requiring reload.
  useEffect(() => {
    if (
      isAuthLoading ||
      !isAuthenticated ||
      (userRedux?.id && userRedux?.profileSource !== "token-fallback")
    ) {
      return;
    }

    let cancelled = false;
    const retryDelays = [0, 300, 700, 1200, 2000, 3000, 5000, 8000, 12000];

    const hydrateProfile = async () => {
      for (let i = 0; i < retryDelays.length; i += 1) {
        if (cancelled) return;

        if (retryDelays[i] > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelays[i]));
          if (cancelled) return;
        }

        try {
          const profile = await dispatch(getProfile());
          if (profile?.id) {
            return;
          }
        } catch {
          // noop: keep retrying within the retry window
        }
      }
    };

    hydrateProfile();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthLoading, isAuthenticated, userRedux?.id]);

  // Suppress benign ResizeObserver loop errors that can surface from UI libraries
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.message || "";
      if (
        msg.includes(
          "ResizeObserver loop completed with undelivered notifications",
        ) ||
        msg.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  // Registration completeness check
  useEffect(() => {
    if (!isAuthenticated || !userRedux) {
      return;
    }

    const completionIdentity = registrationCompletionIdentities[0] || null;
    const completionKey = completionIdentity
      ? `registrationCompleted:${completionIdentity}`
      : null;

    const registrationCompletedBackend =
      registrationCompletedBackendWithRequiredFields;
    if (registrationCompletedBackend && completionIdentity) {
      try {
        const registrationCompletedSessionKey = `registrationCompletedSession:${completionIdentity}`;
        sessionStorage.setItem(registrationCompletedSessionKey, "true");
      } catch {}
      try {
        if (completionKey) {
          localStorage.setItem(completionKey, "true");
        }
      } catch {}
    }
  }, [
    isAuthenticated,
    registrationCompletedBackendWithRequiredFields,
    registrationCompletionIdentities,
    userRedux,
  ]);

  // Dashboard-specific reminder now handles modal; keep app-level modal disabled by default

  useEffect(() => {
    return attachApiInterceptors(axios, "global");
  }, []);

  // Shows the message apiInterceptors.js queues before a hard
  // window.location.href redirect (e.g. a 404 on a tampered game/challenge
  // URL) -- that navigation tears the page down before any toast fired
  // beforehand could render, so it's queued in sessionStorage and shown
  // once, here, after landing on the destination page.
  useEffect(() => {
    let pending = null;
    try {
      const raw = sessionStorage.getItem(PENDING_TOAST_KEY);
      if (raw) {
        pending = JSON.parse(raw);
        sessionStorage.removeItem(PENDING_TOAST_KEY);
      }
    } catch {}

    if (pending?.message) {
      notify(pending.message, { variant: pending.variant || "error" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartExploring = () => {
    navigate("/lists", { replace: true, state: null });
  };

  if (
    isAuthLoading &&
    location.pathname !== "/" &&
    !isSharedResultPage &&
    !isSharedGuestGamePage &&
    !isStandalonePublicPage
  ) {
    return <LoadingFallback />;
  }

  return (
    <RegistrationProvider
      storageKey={registrationStorageKey}
      shouldReset={shouldResetRegistration}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ScrollToTop />
          {/* Registration Success Modal */}
          <RegistrationCompletionModal
            open={showSuccessModal}
            variant="welcome"
            onClose={() => {}}
            onContinue={handleStartExploring}
          />
          {/* Header */}
          {renderHeaderFooter &&
            !hideAppShell &&
            !isRegistrationPage &&
            !registrationGateActive && (
              <header className="sticky-header-box">
                <Suspense fallback={<LoadingFallback />}>
                  <Header drawerWidth={240} user={userRedux} />
                </Suspense>
              </header>
            )}
          {isAuthenticated &&
            !isRegistrationPage &&
            !registrationGateActive && (
              <div className="profile-completion-reminder-slot">
                <AnnouncementSlot
                  user={userRedux}
                  enabled={!showSuccessModal && !hideShellForLogout}
                  showTrial={renderHeaderFooter && !hideAppShell}
                  showProfileBanner={
                    normalizePathname(location.pathname) === "/lists"
                  }
                />
                <ProfileCompletionReminder
                  user={userRedux}
                  enabled={!showSuccessModal && !hideShellForLogout}
                  showBanner={false}
                  showModal={
                    !["/lists", "/profile/edit"].includes(normalizePathname(location.pathname))
                  }
                />
              </div>
            )}
          {!hideAppShell &&
            isAuthenticated &&
            (isRegistrationPage || registrationGateActive) && (
              <RegistrationProfileMenu
                user={userRedux}
                onLogout={() => dispatch(userLogout())}
              />
            )}
          {/* Main Content */}
          <main
            className={`main-content-app${
              isRegistrationPage ? " main-content-app--registration" : ""
            }${
              isSharedResultPage || isSharedGuestGamePage
                ? " main-content-app--shared-result"
                : ""
            }${isGamePlayPage ? " main-content-app--game-play" : ""}`}
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={routeLoadingFallback}>
                <Routes>
                  {publicRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.component}
                    />
                  ))}
                  {onlyLoggedOutRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <OnlyForLoggedOut
                          element={route.component}
                          user={userRedux}
                        />
                      }
                    />
                  ))}
                  {protectedRoutes.map((route) =>
                    route.subRoutes ? (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <ProtectedRoute>{route.component}</ProtectedRoute>
                        }
                      >
                        {route.subRoutes.map((subRoute) => (
                          <Route
                            key={subRoute.path}
                            path={subRoute.path}
                            element={subRoute.component}
                          />
                        ))}
                      </Route>
                    ) : (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <ProtectedRoute>{route.component}</ProtectedRoute>
                        }
                      />
                    ),
                  )}
                  <Route path="*" element={<NoPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </main>
          {/* Footer */}
          {renderHeaderFooter &&
            !hideAppShell &&
            !isRegistrationPage &&
            !registrationGateActive && (
              <footer>
                <Suspense fallback={<LoadingFallback />}>
                  <Footer projectName={projectName} />
                </Suspense>
              </footer>
            )}
        </ErrorBoundary>
        {/* Back to Top */}
        {!hideAppShell && !isRegistrationPage && !registrationGateActive && (
          <BackToTop />
        )}
        {!hideAppShell && (!isRegistrationPage || isAuthenticated) && (
          <FeedbackButton />
        )}
        {!hideAppShell && !isRegistrationPage && !registrationGateActive && (
          <CookieBanner />
        )}
      </Box>
    </RegistrationProvider>
  );
};

const RegistrationProfileMenu = ({ user, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const profile = useReduxUserProfile();
  const { state: registrationState } = useRegistration();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    onLogout?.();
  };

  const handleContactUs = () => {
    handleClose();
    navigate("/contact-us");
  };

  const registrationProfilePicture =
    registrationState?.formData?.profilePicture;
  const hasRegistrationProfilePicture =
    registrationProfilePicture !== undefined &&
    registrationProfilePicture !== null;
  const registrationAvatarSrc = String(registrationProfilePicture || "").trim();
  const persistedAvatarSrc =
    profile?.profilePicture ||
    profile?.avatarURL ||
    profile?.avatarUrl ||
    user?.profilePicture ||
    user?.profile?.profilePicture ||
    user?.avatarURL ||
    user?.avatarUrl ||
    user?.profile?.avatarURL ||
    user?.profile?.avatarUrl ||
    "";
  const avatarSrc = hasRegistrationProfilePicture
    ? registrationAvatarSrc
    : persistedAvatarSrc;
  const fullName =
    profile?.displayName ||
    profile?.name ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
    user?.displayName ||
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "Full Name";
  const username =
    profile?.username ||
    profile?.userName ||
    profile?.preferredUsername ||
    profile?.email ||
    user?.username ||
    user?.userName ||
    user?.preferredUsername ||
    user?.email ||
    "username";
  const avatarAlt = username || "Profile";

  return (
    <Box
      sx={{
        position: "fixed",
        top: { xs: 12, md: 18 },
        right: { xs: 12, md: 28 },
        zIndex: 1300,
      }}
    >
      <button
        type="button"
        className="registration-profile-trigger"
        onClick={handleOpen}
        aria-label="Open profile menu"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
      >
        <Avatar src={avatarSrc} alt={avatarAlt} />
        <Typography fontWeight={700} component="span">
          {fullName}
        </Typography>
        <KeyboardArrowDownIcon className="registration-profile-chevron" />
      </button>
      <MuiMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className: "registration-profile-menu-paper",
          sx: {
            borderRadius: "14px",
            width: 224,
            mt: 1,
            overflow: "hidden",
            border: "1px solid #e6e8ea",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.14)",
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box className="registration-profile-menu-header">
          <Typography className="registration-profile-menu-name">
            {fullName}
          </Typography>
          <Typography className="registration-profile-menu-username">
            {username}
          </Typography>
        </Box>
        <MenuItem
          className="registration-profile-menu-item"
          onClick={handleContactUs}
        >
          Contact Us
        </MenuItem>
        <MenuItem
          className="registration-profile-menu-item"
          onClick={handleLogout}
        >
          Log out
        </MenuItem>
      </MuiMenu>
    </Box>
  );
};

export const ReactContent = () => {
  // redux
  const userRedux = useReduxUser();
  const location = useLocation();
  const routeLoadingFallback =
    location.pathname === "/" ? <HomeSkeleton /> : <LoadingFallback />;

  return (
    <main className="main-content-app">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={routeLoadingFallback}>
          <Routes>
            {publicRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.component}
              />
            ))}
            {onlyLoggedOutRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <OnlyForLoggedOut
                    element={route.component}
                    user={userRedux}
                  />
                }
              />
            ))}
            {protectedRoutes.map((route) =>
              route.subRoutes ? (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      element={route.component}
                      user={userRedux}
                    />
                  }
                >
                  {route.subRoutes.map((subRoute) => (
                    <Route
                      key={subRoute.path}
                      path={subRoute.path}
                      element={subRoute.component}
                    />
                  ))}
                </Route>
              ) : (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      element={route.component}
                      user={userRedux}
                    />
                  }
                />
              ),
            )}
            <Route path="*" element={<NoPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </main>
  );
};

export const ReactHeader = () => {
  // redux
  const userRedux = useReduxUser();

  return (
    <header className="sticky-header-box">
      <Suspense fallback={<LoadingFallback />}>
        <Header drawerWidth={240} user={userRedux} />
      </Suspense>
    </header>
  );
};

export const ReactFooter = () => {
  return (
    <footer>
      <Suspense fallback={<LoadingFallback />}>
        <Footer projectName={projectName} />
      </Suspense>
    </footer>
  );
};

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div role="alert" className="p-4">
      <p>Something went wrong:</p>
      <pre>{JSON.stringify(error.message)}</pre>
      <Button color="error" variant="outlined" onClick={resetErrorBoundary}>
        Try again
      </Button>
    </div>
  );
};

export default App;
